import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import _noop from 'lodash/noop';

import COLORS from '@mlb/react-patterns/style/universal.color';
import { Wrapper } from '@mlb/react-patterns/components/AnimatedModal/Header/header.style';
import { ContentWrapper } from '@mlb/react-patterns/components/AnimatedModal/animatedModal.style';
import { AnimateFrom } from '@mlb/react-patterns/components/AnimatedModal/animatedModal.types.ts';
import ModalComponent from '@mlb/react-patterns/components/AnimatedModal/animatedModal.tsx';
import { useKeyPress } from 'bdvp-common/src/hooks';
import { keyConstants } from 'bdvp-common/src/utils';
import { useDictionary } from 'bdvp-common/src/hooks/config';

import ModalContent from './ModalContent';
import Footer from './Footer';

const Modal = styled(ModalComponent)`
    @supports (padding-top: constant(safe-area-inset-top)) {
        --safe-area-inset-top: constant(safe-area-inset-top, 0);
        height: calc(99% - var(--safe-area-inset-top));
        padding-bottom: constant(safe-area-inset-bottom, 0);
    }

    @supports (padding-top: env(safe-area-inset-top)) {
        --safe-area-inset-top: env(safe-area-inset-top, 0);
        height: calc(99% - var(--safe-area-inset-top));
        padding-bottom: env(safe-area-inset-bottom, 0);
    }

    ${Wrapper} {
        border-bottom: 1px solid ${COLORS.mlbGrey6};
        flex-shrink: 0;
    }

    ${ContentWrapper} {
        height: 100%;
    }
`;

const { TAB } = keyConstants;
const keysToTrack = [TAB];

const ReelModal = (props) => {

    const {
        button,
        isOpen,
        onClosed,
        reel,
        clipsData,
        clipsLoading,
        removeClip,
        reorder,
        openReelCreationModal,
        openClipTrimming,
        isLoggedIn,
        userId,
    } = props;

    const {
        create_reel_title: createReelTitle = 'Create Your Own Reel',
    } = useDictionary();

    const closeButtonRef = React.useRef(null);
    const viewReelsRef = React.useRef(null);

    const keyPressCallbackProps = {
        cb: (event) => {

            if (event.target === viewReelsRef.current) {

                event.preventDefault();
                closeButtonRef.current.focus();

            }

        },
        shiftCB: (event) => {

            if (event.target === closeButtonRef.current) {

                event.preventDefault();
                viewReelsRef.current.focus();

            }

        },
    };

    useKeyPress(keysToTrack, keyPressCallbackProps);

    return (
        <>
            {button}
            <Modal
                open={isOpen}
                width={'100%'}
                height={'99%'}
                overlay={true}
                animate={true}
                animateFrom={AnimateFrom.BOTTOM}
                animationDuration={0.3}
                position={{
                    bottom: 0,
                    left: 0,
                }}
                handlers={{
                    onOpened: () => {

                        closeButtonRef.current.focus();

                    },
                    onClosed: onClosed,
                }}
                headerTitle={createReelTitle}
                closeButtonRef={closeButtonRef}
                modalFooter={
                    <Footer
                        reel={reel}
                        viewReelsRef={viewReelsRef}
                        openReelCreationModal={openReelCreationModal}
                        isLoggedIn={isLoggedIn}
                        userId={userId}
                        closeReelCreation={onClosed}
                    />
                }
                closeButtonRight={true}
            >
                <ModalContent
                    clipsData={clipsData}
                    clipsLoading={clipsLoading}
                    reel={reel}
                    removeClip={removeClip}
                    reorder={reorder}
                    openClipTrimming={openClipTrimming}
                />
            </Modal>
        </>
    );

};

ReelModal.propTypes = {
    button: PropTypes.element.isRequired,
    removeClip: PropTypes.func.isRequired,
    reorder: PropTypes.func.isRequired,
    openReelCreationModal: PropTypes.func.isRequired,
    openClipTrimming: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired,
    reel: PropTypes.object.isRequired,
    clipsData: PropTypes.array.isRequired,
    clipsLoading: PropTypes.bool.isRequired,
    onClosed: PropTypes.func,
    isLoggedIn: PropTypes.bool.isRequired,
    userId: PropTypes.string.isRequired,
};

ReelModal.defaultProps = {
    onClosed: _noop,
};

export default ReelModal;
